import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Text,
  TextInput,
  Button,
  Group,
  Popover,
  NumberInput,
  Select,
  UnstyledButton,
} from '@mantine/core';
import { IconArrowBarRight, IconChevronDown, IconX } from '@tabler/icons-react';
import type { LocationTreeNode } from '../../../api/schedulingApis';
import { CreatableLocationTypeSelect } from '../../../location-hierarchy/CreatableLocationTypeSelect';
import { LocationKeyboardShortcutsContent } from './LocationKeyboardShortcutsModal';
import {
  collectDescendantIds,
  getIdsSortedByDepthDescending,
} from '../../../location-hierarchy/aiReviewUtils';
import { notifications } from '@mantine/notifications';
import { PermissionRestrictedTooltip } from '../../../permissions/scheduler';
import {
  collectLocationTypes,
  countBranchNodes,
  countDirectChildren,
  expandNameTemplate,
  formatLocationTypeLabel,
  getAncestorBreadcrumb,
  isProjectRootNode,
  normalizeLocationNameInput,
  previewNameTemplate,
  appendFormerNameAsAlias,
} from '../logic/locationManagementUtils';
import {
  annotateLocationTypeOptions,
  canDeleteLocationNode,
  canPlaceLocationType,
  findParentNode,
  getFloorNumberFromMeta,
  isFloorLocationType,
  normalizeLocationTypeKey,
  resolveLocationTypeInput,
  withFloorNumberMeta,
} from '../logic/locationTypeRules';

function getNodeAliases(node: (Partial<LocationTreeNode> & Record<string, unknown>) | null | undefined): string[] {
  if (!node) return [];
  const raw =
    node.location_group ??
    (node as { aliases?: string[] }).aliases ??
    (node as { locationGroup?: string[] }).locationGroup ??
    [];
  if (!Array.isArray(raw)) return [];
  return raw.map((a) => String(a).trim()).filter(Boolean);
}

export interface PendingLocationAddDraft {
  parent: LocationTreeNode;
  name: string;
  locationType: string;
  onNameChange: (name: string) => void;
  onTypeChange: (locationType: string) => void;
  onConfirm: () => void;
}

export interface LocationSelectionEditPanelProps {
  selected?: LocationTreeNode;
  pendingAdd?: PendingLocationAddDraft | null;
  treeData: LocationTreeNode[];
  onApplyNodeUpdate: (payload: {
    nodeId: string;
    name: string;
    locationType: string;
    aliases: string[];
    originalName: string;
    meta?: Record<string, unknown>;
  }) => void;
  onUpdateAllByNameType: (name: string, locationType: string) => void;
  onRemoveByIds: (ids: string[]) => void;
  onCloneNode: (nodeId: string) => void;
  /** Called before remove so the parent can move selection to the previous node. */
  onBeforeDelete?: (node: LocationTreeNode) => void;
  onCopyNode?: () => void;
  onPasteNode?: () => void;
  canPaste?: boolean;
  canEdit?: boolean;
  canCreate?: boolean;
  canDelete?: boolean;
  onGenerateChildren?: (params: {
    parent: LocationTreeNode;
    locationType: string;
    names: string[];
  }) => void;
  shortcutsExpanded?: boolean;
  onShortcutsExpandedChange?: (expanded: boolean) => void;
  onClosePanel?: () => void;
  /** Shown inside the details body, under the Location Details header. */
  headerNotice?: React.ReactNode;
}

export function LocationSelectionEditPanel({
  selected,
  pendingAdd = null,
  treeData,
  onApplyNodeUpdate,
  onUpdateAllByNameType,
  onRemoveByIds,
  onCloneNode,
  onBeforeDelete,
  onCopyNode,
  onPasteNode,
  canPaste = false,
  canEdit = true,
  canCreate = true,
  canDelete = true,
  onGenerateChildren,
  shortcutsExpanded = false,
  onShortcutsExpandedChange,
  onClosePanel,
  headerNotice = null,
}: LocationSelectionEditPanelProps) {
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('project');
  const [editAliases, setEditAliases] = useState<string[]>([]);
  const [aliasInput, setAliasInput] = useState('');
  const [editFloorNumber, setEditFloorNumber] = useState<number | string>('');
  const [deletePopoverOpen, setDeletePopoverOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<LocationTreeNode | null>(null);
  const [genType, setGenType] = useState('general');
  const [genCount, setGenCount] = useState<number | string>(8);
  const [genTemplate, setGenTemplate] = useState('Level {01}');
  const [generateSectionOpen, setGenerateSectionOpen] = useState(false);
  const [locationDetailsExpanded, setLocationDetailsExpanded] = useState(true);
  const editNameInputRef = useRef<HTMLInputElement | null>(null);
  const isEditingDetailsRef = useRef(false);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isPendingAdd = pendingAdd != null;
  const editTarget = isPendingAdd ? null : (selected ?? null);
  const panelNode = isPendingAdd ? pendingAdd!.parent : editTarget;

  const selectedLocationId = panelNode?.location_id != null ? String(panelNode.location_id) : '';
  const isRootNode = Boolean(editTarget && isProjectRootNode(editTarget));
  const nodeDeletable = Boolean(editTarget && canDeleteLocationNode(editTarget, treeData));
  const parentNode = useMemo(() => {
    if (isPendingAdd) return pendingAdd!.parent;
    if (!editTarget) return null;
    return findParentNode(treeData, editTarget);
  }, [isPendingAdd, pendingAdd, editTarget, treeData]);

  useEffect(() => {
    if (isPendingAdd || isEditingDetailsRef.current || !editTarget) return;
    setEditName(editTarget.name ?? '');
    setEditType(editTarget.location_type || 'project');
    setEditAliases(getNodeAliases(editTarget as unknown as Record<string, unknown>));
    setAliasInput('');
    const floorNo = getFloorNumberFromMeta(editTarget.meta);
    setEditFloorNumber(floorNo ?? '');
  }, [isPendingAdd, editTarget, selectedLocationId, editTarget?.name, editTarget?.location_type, editTarget?.location_group, editTarget?.meta]);

  const locationTypeOptions = useMemo(() => {
    const types = collectLocationTypes(treeData);
    if (!types.includes('project')) types.unshift('project');
    if (!types.includes('general')) types.push('general');
    if (!types.includes('block')) types.push('block');
    if (!types.includes('floor')) types.push('floor');
    return [...new Set(types)].sort().map((t) => ({
      value: t,
      label: formatLocationTypeLabel(t),
    }));
  }, [treeData]);

  const locationTypeOptionsForSelect = useMemo(() => {
    if (isPendingAdd) {
      return annotateLocationTypeOptions(
        locationTypeOptions.filter((o) => o.value.toLowerCase() !== 'project'),
        pendingAdd.parent,
        treeData,
      );
    }
    if (isRootNode) return [{ value: 'project', label: 'Project' }];
    const annotated = annotateLocationTypeOptions(
      locationTypeOptions.filter((o) => o.value.toLowerCase() !== 'project'),
      parentNode,
      treeData,
      { excludeNodeId: selectedLocationId },
    );
    // Keep the node's current type selectable even if placement would be invalid.
    const current = normalizeLocationTypeKey(editTarget?.location_type);
    return annotated.map((o) =>
      normalizeLocationTypeKey(o.value) === current ? { ...o, disabled: false } : o,
    );
  }, [
    locationTypeOptions,
    isPendingAdd,
    pendingAdd,
    isRootNode,
    parentNode,
    treeData,
    selectedLocationId,
    editTarget?.location_type,
  ]);

  const childTypeOptions = useMemo(() => {
    if (isPendingAdd) return locationTypeOptionsForSelect.filter((o) => !o.disabled);
    if (!editTarget) return [];
    return annotateLocationTypeOptions(
      locationTypeOptions.filter((o) => o.value.toLowerCase() !== 'project'),
      editTarget,
      treeData,
    ).filter((o) => !o.disabled);
  }, [locationTypeOptions, isPendingAdd, locationTypeOptionsForSelect, editTarget, treeData]);

  useEffect(() => {
    if (childTypeOptions.length === 0) return;
    if (!childTypeOptions.some((o) => o.value === genType)) {
      setGenType(childTypeOptions[0].value);
    }
  }, [childTypeOptions, genType]);

  useEffect(() => {
    if (shortcutsExpanded) {
      setLocationDetailsExpanded(false);
      return;
    }
    setLocationDetailsExpanded(true);
  }, [shortcutsExpanded]);

  const breadcrumb = useMemo(() => {
    if (isPendingAdd) {
      return getAncestorBreadcrumb(treeData, String(pendingAdd.parent.location_id));
    }
    return getAncestorBreadcrumb(treeData, selectedLocationId);
  }, [isPendingAdd, pendingAdd, treeData, selectedLocationId]);

  const breadcrumbText =
    breadcrumb.map((n) => n.name).filter(Boolean).join(' / ') ||
    (isPendingAdd ? pendingAdd.parent.name || '' : editTarget?.name || '');

  const directChildren = isPendingAdd || !editTarget ? 0 : countDirectChildren(editTarget);
  const branchCount = isPendingAdd || !editTarget ? 0 : countBranchNodes(editTarget);

  const genQty = Math.max(1, Math.min(200, Number(genCount) || 1));
  const genPreview = previewNameTemplate(genTemplate, genQty);
  const showFloorNumber = isPendingAdd
    ? isFloorLocationType(pendingAdd.locationType)
    : isFloorLocationType(editType);
  const displayName = isPendingAdd ? pendingAdd.name : editName;
  const displayType = isPendingAdd ? pendingAdd.locationType : editType;
  const aliasFieldDisabled = isPendingAdd || !canEdit;

  const commitDetails = useCallback(
    (name: string, locationType: string, aliases: string[], floorNumber: number | string) => {
      if (!editTarget || isPendingAdd) return;
      const trimmedName = name.trim();
      if (!trimmedName || editTarget.location_id == null) return;
      const knownTypes = locationTypeOptions.map((o) => o.value);
      const type = isRootNode
        ? 'project'
        : resolveLocationTypeInput(locationType || 'general', knownTypes) || 'general';
      const typeChanged =
        !isRootNode &&
        normalizeLocationTypeKey(type) !== normalizeLocationTypeKey(editTarget.location_type);

      if (typeChanged) {
        const placement = canPlaceLocationType({
          childType: type,
          parent: parentNode,
          tree: treeData,
          excludeNodeId: selectedLocationId,
        });
        if (!placement.ok) {
          notifications.show({ message: placement.reason, color: 'red' });
          setEditType(editTarget.location_type || 'general');
          return;
        }
      }

      const originalName = (editTarget.name ?? '').trim();
      const nextAliases = appendFormerNameAsAlias(aliases, originalName, trimmedName);
      setEditAliases(nextAliases);

      const parsedFloor =
        isFloorLocationType(type) && floorNumber !== '' && floorNumber != null
          ? Number(floorNumber)
          : null;
      const meta = withFloorNumberMeta(
        editTarget.meta,
        parsedFloor != null && Number.isFinite(parsedFloor) ? parsedFloor : null,
      );

      onApplyNodeUpdate({
        nodeId: String(editTarget.location_id),
        name: trimmedName,
        locationType: type,
        aliases: nextAliases,
        originalName,
        meta,
      });
    },
    [
      editTarget,
      isPendingAdd,
      isRootNode,
      parentNode,
      treeData,
      selectedLocationId,
      onApplyNodeUpdate,
      locationTypeOptions,
    ],
  );

  const handleNameBlur = useCallback(() => {
    if (!editTarget) return;
    blurTimerRef.current = setTimeout(() => {
      isEditingDetailsRef.current = false;
      blurTimerRef.current = null;
      if ((editName ?? '').trim() !== (editTarget.name ?? '').trim()) {
        commitDetails(editName, editType, editAliases, editFloorNumber);
      }
    }, 120);
  }, [editName, editType, editAliases, editFloorNumber, editTarget, commitDetails]);

  const handleTypeChange = useCallback(
    (v: string) => {
      if (isRootNode || !editTarget) return;
      isEditingDetailsRef.current = true;
      const knownTypes = locationTypeOptions.map((o) => o.value);
      const type = resolveLocationTypeInput(v || 'general', knownTypes) || 'general';
      setEditType(type);
      const nextFloor = isFloorLocationType(type) ? editFloorNumber : '';
      if (!isFloorLocationType(type)) setEditFloorNumber('');
      commitDetails(editName || editTarget.name || '', type, editAliases, nextFloor);
      // Allow sync effect after store catches up; keep ref true briefly so it doesn't clobber.
      window.setTimeout(() => {
        isEditingDetailsRef.current = false;
      }, 0);
    },
    [
      isRootNode,
      editName,
      editTarget,
      editAliases,
      editFloorNumber,
      commitDetails,
      locationTypeOptions,
    ],
  );

  const handleAliasKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      const v = aliasInput.trim();
      if (!v || editAliases.includes(v)) return;
      const next = [...editAliases, v];
      setEditAliases(next);
      setAliasInput('');
      commitDetails(editName || editTarget?.name || '', editType, next, editFloorNumber);
    },
    [aliasInput, editAliases, editName, editTarget?.name, editType, editFloorNumber, commitDetails],
  );

  const handleRemoveAlias = useCallback(
    (index: number) => {
      const next = editAliases.filter((_, i) => i !== index);
      setEditAliases(next);
      commitDetails(editName || editTarget?.name || '', editType, next, editFloorNumber);
    },
    [editAliases, editName, editTarget?.name, editType, editFloorNumber, commitDetails],
  );

  const handleFloorNumberBlur = useCallback(() => {
    if (!editTarget) return;
    const base = getFloorNumberFromMeta(editTarget.meta);
    const next =
      editFloorNumber === '' || editFloorNumber == null ? null : Number(editFloorNumber);
    const nextNorm = next != null && Number.isFinite(next) ? next : null;
    if (base === nextNorm) return;
    commitDetails(editName || editTarget.name || '', editType, editAliases, editFloorNumber);
  }, [editTarget, editFloorNumber, editName, editType, editAliases, commitDetails]);

  const handleCloneLocation = useCallback(() => {
    if (!editTarget || editTarget.location_id == null || isRootNode) return;
    onCloneNode(String(editTarget.location_id));
    notifications.show({
      message: 'Location duplicated in draft. Save changes to persist.',
      color: 'teal',
    });
  }, [editTarget, isRootNode, onCloneNode]);

  const openDeletePopover = useCallback(() => {
    if (!editTarget || !nodeDeletable) return;
    setLocationToDelete(editTarget);
    setDeletePopoverOpen(true);
  }, [editTarget, nodeDeletable]);

  const closeDeletePopover = useCallback(() => {
    setDeletePopoverOpen(false);
    setLocationToDelete(null);
  }, []);

  const handleConfirmDeleteLocation = useCallback(() => {
    if (!locationToDelete?.location_id || !canDeleteLocationNode(locationToDelete, treeData)) return;
    const descendantIds = collectDescendantIds([locationToDelete]);
    const idsToDelete = new Set<string>([String(locationToDelete.location_id), ...descendantIds]);
    const orderedIds = getIdsSortedByDepthDescending(treeData, idsToDelete);
    onBeforeDelete?.(locationToDelete);
    onRemoveByIds(orderedIds);
    closeDeletePopover();
  }, [locationToDelete, treeData, onRemoveByIds, onBeforeDelete, closeDeletePopover]);

  const handleGenerate = useCallback(() => {
    if (!onGenerateChildren || !canCreate || !editTarget) return;
    const type =
      resolveLocationTypeInput(
        genType || 'general',
        locationTypeOptions.map((o) => o.value),
      ) || 'general';
    const placement = canPlaceLocationType({
      childType: type,
      parent: editTarget,
      tree: treeData,
    });
    if (!placement.ok) {
      notifications.show({ message: placement.reason, color: 'red' });
      return;
    }
    const names = expandNameTemplate(genTemplate, genQty);
    onGenerateChildren({
      parent: editTarget,
      locationType: type,
      names,
    });
  }, [
    onGenerateChildren,
    canCreate,
    genTemplate,
    genQty,
    genType,
    editTarget,
    treeData,
    locationTypeOptions,
  ]);

  void onUpdateAllByNameType;
  void onPasteNode;
  void canPaste;

  if (!panelNode) return null;

  const typeSelectExtras = {
    withCheckIcon: false as const,
    rightSection: <IconChevronDown size={14} stroke={1.75} color="#8B91A0" />,
    rightSectionPointerEvents: 'none' as const,
    renderOption: ({
      option,
      checked,
    }: {
      option: { value: string; label: string };
      checked?: boolean;
    }) => (
      <span
        className={[
          'location-tree-node-mgmt-type-option',
          checked ? 'is-selected' : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {option.label}
      </span>
    ),
  };

  return (
    <div className="location-mgmt-side">
      <div className="location-mgmt-side-header">
        <span className="location-mgmt-side-header-title">Location Details</span>
        {onClosePanel ? (
          <UnstyledButton
            className="location-mgmt-side-close-btn"
            onClick={onClosePanel}
            aria-label="Close location details panel"
          >
            <IconArrowBarRight size={18} stroke={1.5} />
          </UnstyledButton>
        ) : null}
      </div>
      {locationDetailsExpanded ? (
      <div className="location-mgmt-side-body">
        {headerNotice}
        <p className="location-mgmt-side-breadcrumb">{breadcrumbText}</p>

        <Box>
          <span className="location-mgmt-side-label">Location name</span>
          <TextInput
            className="location-mgmt-side-name"
            ref={editNameInputRef}
            value={displayName}
            disabled={isPendingAdd ? !canCreate : !canEdit}
            onChange={(e) => {
              const next = normalizeLocationNameInput(e.currentTarget.value);
              if (isPendingAdd) pendingAdd.onNameChange(next);
              else setEditName(next);
            }}
            onFocus={() => {
              if (blurTimerRef.current) {
                clearTimeout(blurTimerRef.current);
                blurTimerRef.current = null;
              }
              isEditingDetailsRef.current = true;
            }}
            onBlur={() => {
              if (isPendingAdd) {
                isEditingDetailsRef.current = false;
                return;
              }
              handleNameBlur();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            placeholder={isPendingAdd ? 'Location name' : undefined}
          />
        </Box>

        <Box>
          <span className="location-mgmt-side-label">Location type</span>
          {isRootNode ? (
            <TextInput className="location-mgmt-side-field" value="Project" disabled readOnly />
          ) : isPendingAdd ? (
            <Select
              className="location-mgmt-side-field"
              size="sm"
              data={locationTypeOptionsForSelect}
              value={
                resolveLocationTypeInput(
                  displayType || 'general',
                  locationTypeOptionsForSelect.map((o) => o.value),
                ) || 'general'
              }
              onChange={(v) => {
                if (!v) return;
                pendingAdd.onTypeChange(v);
              }}
              disabled={!canCreate}
              searchable
              allowDeselect={false}
              comboboxProps={{ zIndex: 1100 }}
              {...typeSelectExtras}
            />
          ) : (
            <CreatableLocationTypeSelect
              className="location-mgmt-side-field"
              size="sm"
              options={locationTypeOptionsForSelect}
              value={displayType}
              isTypeAllowed={(type) =>
                canPlaceLocationType({
                  childType: type,
                  parent: parentNode,
                  tree: treeData,
                  excludeNodeId: selectedLocationId,
                }).ok
              }
              onChange={(v) => handleTypeChange(v)}
              disabled={!canEdit}
              comboboxProps={{ zIndex: 1100 }}
              {...typeSelectExtras}
            />
          )}
        </Box>

        {showFloorNumber ? (
          <Box>
            <span className="location-mgmt-side-label">Floor index</span>
            <NumberInput
              className="location-mgmt-side-field"
              size="sm"
              value={isPendingAdd ? '' : editFloorNumber}
              onChange={setEditFloorNumber}
              disabled={isPendingAdd || !canEdit}
              placeholder="e.g. 1"
              hideControls
              onFocus={() => {
                isEditingDetailsRef.current = true;
              }}
              onBlur={() => {
                isEditingDetailsRef.current = false;
                handleFloorNumberBlur();
              }}
            />
          </Box>
        ) : null}

        <Box>
          <span className="location-mgmt-side-label">Location aliases</span>
          <div className="location-mgmt-side-alias-field">
            {!isPendingAdd &&
              editAliases.map((a, i) => (
              <span key={`${a}-${i}`} className="location-tree-alias-chip location-mgmt-side-alias-chip">
                {a}
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => handleRemoveAlias(i)}
                    aria-label={`Remove alias ${a}`}
                  >
                    ×
                  </button>
                ) : null}
              </span>
            ))}
            <TextInput
              className="location-mgmt-side-alias-input"
              size="sm"
              value={aliasInput}
              disabled={isPendingAdd || !canEdit}
              variant="unstyled"
              onChange={(e) => setAliasInput(e.currentTarget.value)}
              onKeyDown={handleAliasKeyDown}
              placeholder={
                isPendingAdd
                  ? 'Add after location is created'
                  : editAliases.length === 0
                    ? 'Type and press Enter to add'
                    : 'Add alias…'
              }
              onFocus={() => {
                isEditingDetailsRef.current = true;
              }}
              onBlur={() => {
                isEditingDetailsRef.current = false;
              }}
            />
          </div>
        </Box>

        {!isPendingAdd ? (
        <div className="location-mgmt-side-actions">
          <Button
            className="location-mgmt-side-action"
            variant="default"
            fullWidth
            onClick={onCopyNode}
            disabled={!onCopyNode}
          >
            Copy
          </Button>
          <PermissionRestrictedTooltip restricted={!canCreate} fullWidth>
            <Button
              className="location-mgmt-side-action"
              variant="default"
              fullWidth
              onClick={handleCloneLocation}
              disabled={!canCreate || isRootNode}
            >
              {isFloorLocationType(editType) ? 'Add floor' : 'Duplicate'}
            </Button>
          </PermissionRestrictedTooltip>
          <PermissionRestrictedTooltip restricted={!canDelete} fullWidth>
            <Popover opened={deletePopoverOpen} onClose={closeDeletePopover} position="bottom" zIndex={1100}>
              <Popover.Target>
                <Button
                  className="location-mgmt-side-action location-mgmt-side-action--remove"
                  variant="default"
                  onClick={openDeletePopover}
                  disabled={!canDelete || !nodeDeletable}
                  fullWidth
                >
                  Delete
                </Button>
              </Popover.Target>
              <Popover.Dropdown className="location-mgmt-side-delete-popover">
                <Text size="sm" c="dimmed">
                  Delete &quot;{locationToDelete?.name ?? ''}&quot; and all sub-locations?
                </Text>
                <Group grow mt="md" gap="xs">
                  <Button
                    className="location-mgmt-side-action"
                    variant="default"
                    onClick={closeDeletePopover}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="location-mgmt-side-action location-mgmt-side-action--remove"
                    variant="default"
                    onClick={handleConfirmDeleteLocation}
                  >
                    Delete
                  </Button>
                </Group>
              </Popover.Dropdown>
            </Popover>
          </PermissionRestrictedTooltip>
        </div>
        ) : (
        <div className="location-mgmt-side-actions">
          <PermissionRestrictedTooltip restricted={!canCreate} fullWidth>
            <Button
              className="location-mgmt-side-add-btn"
              fullWidth
              onClick={() => pendingAdd.onConfirm()}
              disabled={!canCreate || !(pendingAdd.name ?? '').trim()}
            >
              Create this location
            </Button>
          </PermissionRestrictedTooltip>
        </div>
        )}

        {(directChildren > 0 || branchCount > 0) && (
        <div className="location-mgmt-side-stats">
          {directChildren > 0 ? (
          <div className="location-mgmt-side-stat">
            <span className="location-mgmt-side-stat-label">Direct children</span>
            <span className="location-mgmt-side-stat-count">{directChildren}</span>
          </div>
          ) : null}
          {branchCount > 0 ? (
          <div className="location-mgmt-side-stat">
            <span className="location-mgmt-side-stat-label">In this branch</span>
            <span className="location-mgmt-side-stat-count">{branchCount}</span>
          </div>
          ) : null}
        </div>
        )}

        {!isPendingAdd ? (
        <>
        <hr className="location-mgmt-side-divider" />

        {generateSectionOpen ? (
          <div className="location-mgmt-side-generate">
            <div className="location-mgmt-side-generate-header">
              <p className="location-mgmt-side-generate-title">Generate child row</p>
              <UnstyledButton
                className="location-mgmt-side-generate-close"
                onClick={() => setGenerateSectionOpen(false)}
                aria-label="Close generate child row section"
              >
                <IconX size={14} stroke={1.75} />
              </UnstyledButton>
            </div>
            <Group grow gap="xs" align="flex-start">
              <Box style={{ flex: 1, minWidth: 0 }}>
                <span className="location-mgmt-side-label">Location type</span>
                <CreatableLocationTypeSelect
                  className="location-mgmt-side-field"
                  size="sm"
                  options={childTypeOptions}
                  value={genType}
                  isTypeAllowed={(type) =>
                    canPlaceLocationType({
                      childType: type,
                      parent: editTarget,
                      tree: treeData,
                    }).ok
                  }
                  onChange={(v) =>
                    setGenType(
                      resolveLocationTypeInput(
                        v || 'general',
                        locationTypeOptions.map((o) => o.value),
                      ) || 'general',
                    )
                  }
                  comboboxProps={{ zIndex: 1100 }}
                  {...typeSelectExtras}
                />
              </Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <span className="location-mgmt-side-label">No. of locations</span>
                <NumberInput
                  className="location-mgmt-side-field location-mgmt-side-qty"
                  size="sm"
                  value={genCount}
                  onChange={setGenCount}
                  min={1}
                  max={200}
                  hideControls
                />
              </Box>
            </Group>
            <TextInput
              className="location-mgmt-side-field"
              size="sm"
              value={genTemplate}
              onChange={(e) => setGenTemplate(e.currentTarget.value)}
              placeholder="Name template"
            />
            <p className="location-mgmt-side-generate-preview">{genPreview}</p>
            <PermissionRestrictedTooltip restricted={!canCreate} fullWidth>
              <Button
                className="location-mgmt-side-add-btn"
                fullWidth
                onClick={handleGenerate}
                disabled={!canCreate || !onGenerateChildren || childTypeOptions.length === 0}
              >
                Generate {genQty} {formatLocationTypeLabel(genType)} locations
              </Button>
            </PermissionRestrictedTooltip>
          </div>
        ) : (
          <button
            type="button"
            className="location-mgmt-side-generate-toggle"
            onClick={() => setGenerateSectionOpen(true)}
          >
            Generate child row
          </button>
        )}
        </>
        ) : null}
      </div>
      ) : null}

      <div className="location-mgmt-side-footer">
        <UnstyledButton
          className="location-mgmt-side-shortcuts"
          onClick={() => onShortcutsExpandedChange?.(!shortcutsExpanded)}
          aria-expanded={shortcutsExpanded}
        >
          <span>Keyboard Shortcuts</span>
          <IconChevronDown
            size={14}
            stroke={1.75}
            className={`location-mgmt-side-shortcuts-chevron${shortcutsExpanded ? ' location-mgmt-side-shortcuts-chevron--open' : ''}`}
          />
        </UnstyledButton>
      </div>

      {shortcutsExpanded ? (
        <div className="location-mgmt-side-shortcuts-panel">
          <LocationKeyboardShortcutsContent />
        </div>
      ) : null}
    </div>
  );
}
